import { ChangeDetectionStrategy, Component, inject, input, OnDestroy, OnInit } from '@angular/core';
import { IItemScopingCategory } from '@app/shared/interfaces/iitem-scoping-category';
import { IProjectTankBatteryScopingItemDto } from '@app/shared/models/dtos/project-tank-battery-scoping-item';
import { AgGridAngular } from 'ag-grid-angular';
import { CellValueChangedEvent, GridApi, GridSizeChangedEvent, ICellRendererParams } from 'ag-grid-community';
import { Validator } from 'fluentvalidation-ts';
import { Subscription } from 'rxjs';
import { ProjectScopingBrokerService } from '../../services/project-scoping-broker.service';
import { IProjectScopingItemNoteSaveRequest, IProjectScopingItemSaveRequest, ProjectScopingService } from '../../services/project-scoping.service';
import { ProjectScopingItemsGridOptions, ProjectScopingItemsGridOptionService } from './columns';

@Component({
  selector: 'app-project-scoping-items',
  templateUrl: './project-scoping-items.component.html',
  styleUrls: ['./project-scoping-items.component.scss'],
  providers: [ProjectScopingItemsGridOptionService],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    AgGridAngular,
  ],
})
export class ProjectScopingItemsComponent implements OnInit, OnDestroy {
  private projectScopingService = inject(ProjectScopingService);
  private projectScopingItemsGridOptionService = inject(ProjectScopingItemsGridOptionService);
  private broker = inject(ProjectScopingBrokerService);

  readonly projectId = input<number>(undefined);

  public gridOptions: ProjectScopingItemsGridOptions;
  public isLoading: boolean;

  private projectScopingItems: Array<IProjectTankBatteryScopingItemDto>;
  private itemScopingCategories: Array<IItemScopingCategory>;
  private itemScopingCategoriesMap: Map<number, IItemScopingCategory>;
  private gridApi: GridApi;
  private cancelChangesEventSub: Subscription;
  private saveChangesEventSub: Subscription;
  private removeItemEventSub: Subscription;
  private loadingEventSub: Subscription;
  private projectTankBatteryScopingItemValidator = new ProjectTankBatteryScopingItemDtoValidator();

  public ValidationErrors: Map<string, string[]>;

  constructor() {
    this.gridOptions = this.projectScopingItemsGridOptionService.projectScopingItemsGridOptions;

    this.loadingEventSub = this.projectScopingService.LoadingEvent.subscribe(isLoading => {
      this.isLoading = isLoading;
    });
  }

  ngOnInit() {
    this.cancelChangesEventSub = this.broker.CancelChangesEvent.subscribe(s => {
      if (s != null) this.handleCancelChangesEvent(s);
    });

    this.saveChangesEventSub = this.broker.SaveChangesEvent.subscribe(s => {
      if (s != null) this.handleSaveChangesEvent(s);
    });

    this.removeItemEventSub = this.projectScopingItemsGridOptionService.RemoveItemEvent.subscribe(s => {
      if (s != null) this.handleRemoveItemEvent(s);
    });

    this.projectScopingItemsGridOptionService.NoteEvent.subscribe(s => {
      if (s != null && s.action === 'save') {
        this.handleSaveItemNoteEvent(s)
      }
    });

    this.ValidationErrors = new Map<string, Array<string>>();
    this.loadData();
  }

  ngOnDestroy() {
    if (this.cancelChangesEventSub) {
      this.cancelChangesEventSub.unsubscribe();
    }

    if (this.saveChangesEventSub) {
      this.saveChangesEventSub.unsubscribe();
    }

    if (this.removeItemEventSub) {
      this.removeItemEventSub.unsubscribe();
    }

    if (this.loadingEventSub) {
      this.loadingEventSub.unsubscribe();
    }

    this.projectScopingItemsGridOptionService.NoteEvent.unsubscribe();
  }

  public async loadData() {
    await this.projectScopingService.loadData(this.projectId());

    this.projectScopingItemsGridOptionService.ItemsMap = new Map(this.projectScopingService.IItemDtos.map(m => [m.ItemId, m]));
    this.itemScopingCategoriesMap = new Map(this.projectScopingService.IItemScopingCategories.map(m => [m.ItemScopingCategoryId, m]));

    this.itemScopingCategories = this.projectScopingService.IItemScopingCategories;
    this.projectScopingItems = this.projectScopingService.IProjectTankBatteryScopingItemDtos;

    this.loadGrid();
  }

  private loadGrid() {
    this.itemScopingCategories.forEach(cat => {
      //todo - add this to model
      this.projectScopingItems.push({
        ProjectTankBatteryScopingItemId: this.projectScopingService.newProjectTankBatteryScopingItemId--,
        ProjectId: this.projectId(),
        ItemDto: { ItemId: 0, Name: null, ItemNumber: 0, CostCodeCostTypeId: 0, PrimaryUOM: 0, UsePhaseStepStartDate: false },
        ItemScopingCategoryId: cat.ItemScopingCategoryId,
        OilCapacity: null,
        GasCapacity: null,
        WaterCapacity: null,
        BackupQuantity: null,
        Quantity: null,
        ItemScopingCategoryDto: {
          ItemScopingCategoryId: cat.ItemScopingCategoryId,
          Description: cat.Description,
          SortOrder: cat.SortOrder
        },
      });
    });

    this.projectScopingItems.sort((a, b) => {
      if (a.ItemScopingCategoryDto.SortOrder == b.ItemScopingCategoryDto.SortOrder) {
        if (a.ItemId == null) {
          return 1;
        } else if (b.ItemId == null) {
          return -1;
        } else {
          return a.ItemId < b.ItemId ? -1 : 1;
        }
      }

      return a.ItemScopingCategoryDto.SortOrder > b.ItemScopingCategoryDto.SortOrder ? 1 : -1;
    });

    if (!this.gridApi.isDestroyed()) {
      this.gridApi.setGridOption('rowData', this.projectScopingItems);
    }

    this.ValidationErrors = new Map<string, Array<string>>();
  }

  onGridSizeChanged(params: GridSizeChangedEvent) {
    if (this.isLoading) return;
    params.api.sizeColumnsToFit();
  }

  private trackChanges(e: CellValueChangedEvent) {
    if (e.colDef.field === 'ItemId' && e.oldValue !== e.newValue && e.oldValue == null) {
      this.addNewBlankRow(e.data.ItemScopingCategoryId);
    }

    //set scoping-item capacities based on the selected Item
    if (e.colDef.field === 'ItemId' && e.oldValue !== e.newValue) {
      let item = this.projectScopingItemsGridOptionService.ItemsMap.get(e.newValue);
      e.data.OilCapacity = item.OilCapacity;
      e.data.GasCapacity = item.GasCapacity;
      e.data.WaterCapacity = item.WaterCapacity;
      e.data.Quantity = 1;
      e.data.ExistingQuantity = item.ExistingQuantity;
    }

    this.projectScopingService.upsertRecord(e.data.ProjectTankBatteryScopingItemId, e.data);

    this.gridApi.refreshCells();
    this.broker.ProjectScopingItemsGridChangedEvent.next(true);
  }

  private addNewBlankRow(itemScopingCategoryId: number) {
    let newItem = {
      ProjectTankBatteryScopingItemId: this.projectScopingService.newProjectTankBatteryScopingItemId--,
      ProjectId: this.projectId(),
      ItemDto: { ItemId: 0, Name: null, ItemNumber: 0, CostCodeCostTypeId: 0, PrimaryUOM: 0 },
      ItemScopingCategoryId: itemScopingCategoryId,
      OilCapacity: null,
      GasCapacity: null,
      WaterCapacity: null,
      Quantity: null,
      ItemScopingCategoryDto: this.itemScopingCategoriesMap.get(itemScopingCategoryId),
    };

    this.gridApi.applyTransaction({
      add: [newItem]
    });
  }

  private handleCancelChangesEvent(e: any) {
    this.ValidationErrors = new Map<string, string[]>();
    if (!e) return;
    this.projectScopingService.resetChanges();
    this.loadData();
  }

  private async handleSaveChangesEvent(e: any) {
    if (!e) return;

    this.gridApi.stopEditing();
    this.ValidationErrors = new Map<string, Array<string>>();
    let upsertArray = Array.from(this.projectScopingService.editedRecords.values());

    upsertArray.forEach(element => {
      let obj = this.projectTankBatteryScopingItemValidator.validate(element);
      const values = Object.values(obj);
      if (values.length > 0) {
        let messages = new Array<string>();

        values.forEach(message => {
          messages.push(message as string)
        });
        this.ValidationErrors.set(this.projectScopingItemsGridOptionService.ItemsMap.get(element.ItemId).Name + ' has the following error(s):', messages);
      }
    });

    if (this.ValidationErrors.size > 0) {
      return;
    }

    let data: IProjectScopingItemSaveRequest = {
      ProjectId: this.projectId(),
      Upserts: upsertArray,
      Removals: Array.from(this.projectScopingService.removedRecords.values())
    }

    await this.projectScopingService.saveChanges(data);

    this.handleCancelChangesEvent(true);

    this.broker.SaveChangesSuccessfulEvent.next(true);
  }

  private async handleSaveItemNoteEvent(e) {
    let id = e.params.node.data.ProjectTankBatteryScopingItemId ? e.params.node.data.ProjectTankBatteryScopingItemId : e.params.data.ProjectTankBatteryScopingItemId;
    let data: IProjectScopingItemNoteSaveRequest = {
      ProjectTankBatteryScopingItemId: id,
      Note: e.note
    }
    let result = await this.projectScopingService.saveNote(data);
    this.projectScopingItems.find(x => x.ProjectTankBatteryScopingItemId == id).Note = result.Note;
    this.gridApi.refreshCells({ force: true, columns: [e.params.column] });
  }

  private handleRemoveItemEvent(e: ICellRendererParams) {
    if (!e) return;

    this.projectScopingService.removeRecord(e.data.ProjectTankBatteryScopingItemId, e.data);

    this.gridApi.applyTransaction({
      remove: [e.data]
    });

    this.broker.ProjectScopingItemsGridChangedEvent.next(true);
  }

  public onGridReady(params) {
    this.gridApi = params.api;
  }

  public onRowValueChanged(e: CellValueChangedEvent) {
    this.trackChanges(e);
  }
}

export class ProjectTankBatteryScopingItemDtoValidator extends Validator<IProjectTankBatteryScopingItemDto> {
  constructor() {
    super();

    this.ruleFor('BackupQuantity')
      .must((model, field) => {
        if (model === null) { return true; }
        if (model > field.Quantity) {
          return false;
        } return true;
      }).withMessage(`'Backup Quantity' cannot be greater than 'Quantity'`);
  }
}